import React, { useState, useEffect } from "react"
import PropTypes from "prop-types"
import { Dropdown, Button, Tag, Flex } from "antd"
import { DownOutlined } from "@ant-design/icons"
import { useSelector } from "react-redux"
import GroupMenuItems from "./GroupMenuItems"
import styles from "./index.module.css"
import { useTranslation } from "react-i18next"
import { propTypes } from "react-bootstrap/esm/Image"
import { Text } from "ui/Typography"
import { colors } from "themes/variables"

const renderGroupMenuDropdown =
  (groups, selectedGroups, handleSelectedGroupsChange, client, selectedClientIds, moduleName, setDropdownVisible) => () =>
    (
      <GroupMenuItems
        groups={groups}
        selectedGroups={selectedGroups}
        onSelectedGroupsChange={handleSelectedGroupsChange}
        client={client}
        selectedClientIds={selectedClientIds}
        moduleName={moduleName}
        setDropdownVisible={setDropdownVisible}
      />
    )

const GroupDropdown = ({
  action = "",
  menuState = false,
  isMenuListHide = false,
  selectedGroups: propSelectedGroups = [],
  client,
  selectedClientIds,
  onSelectedGroupsChange,
  moduleName,
  setSelectedClientIds,
  groupAction
}) => {
  const { t } = useTranslation()
  const [selectedGroups, setSelectedGroups] = useState([])
  const [dropdownVisible, setDropdownVisible] = useState(false)
  const groups = useSelector((state) => state.groups?.items || [])
  const { adventure_mode } = useSelector((store) => store.user)
  const themeColor = adventure_mode === "sportsman" ? colors.sportsmanBrand : colors.sportswomanBrand
  useEffect(() => {
    setSelectedGroups(propSelectedGroups)
  }, [propSelectedGroups])

  const handleSelectedGroupsChange = (updated) => {
    setSelectedGroups(updated)
    onSelectedGroupsChange?.(updated)
  }
  const handleRemoveSelected = async (val, e) => {
    e.stopPropagation()
    const updatedGroups = selectedGroups.filter((group) => group !== val)
    handleSelectedGroupsChange(updatedGroups)
  }

  return (
    <div className="w-full position-relative">
      {groupAction === "bulk_add" || groupAction === "add" ? (
        <div className="mb-xxs">
          <Text className="fw-500">Group</Text>
        </div>
      ) : null}
      {!menuState ? (
        <Dropdown
          trigger={["click"]}
          open={menuState ? true : dropdownVisible}
          onOpenChange={setDropdownVisible}
          dropdownRender={renderGroupMenuDropdown(
            groups,
            selectedGroups,
            handleSelectedGroupsChange,
            client,
            selectedClientIds,
            moduleName,
            setDropdownVisible
          )}
          className="p-0"
        >
          <div className={styles.selectedGroupsDropdown}>
            <Button className={`justify-between w-full ${adventure_mode === "sportsman" ? styles.btnSportsman : styles.btnSportswoman}`}>
              <Flex gap={4} className={styles.selectedGroupsDropdownItems}>
                {selectedGroups.length > 0
                  ? selectedGroups.map((val) => {
                      const group = groups.find((g) => g.id === val || g.id === val?.id || g.id === val?.group_id)
                      return group ? (
                        <Tag
                          key={val}
                          closable
                          style={{ backgroundColor: group.color + "42", color: group.color }}
                          onClose={(e) => handleRemoveSelected(val, e)}
                          className={styles.groupTag}
                        >
                          {group.name}
                        </Tag>
                      ) : null
                    })
                  : t("clients.select_group")}
              </Flex>
              <DownOutlined />
            </Button>
          </div>
        </Dropdown>
      ) : (
        <>
          <Button
            className={`justify-between w-full mb-xl px-s py-xs h-auto ${isMenuListHide ? styles.noBorder : ""} ${
              styles.customTagDropdown
            }`}
          >
            <Flex wrap="wrap" gap={4}>
              {(() => {
                // Handle no groups selected case
                if (selectedGroups.length === 0) {
                  const placeholderText =
                    groupAction === "bulk_add" || groupAction === "add" ? t("clients.select_group") : t("clients.no_group_available")
                  return placeholderText
                }

                // Handle groups selected case
                return selectedGroups.map((val) => {
                  const group = groups.find((g) => g.id === val || g.id === val?.id || g.id === val?.group_id)
                  return group ? (
                    <Tag
                      key={val}
                      closable
                      color={group.color}
                      style={{ color: group.color, backgroundColor: group.color + "26" }}
                      onClose={(e) => handleRemoveSelected(val, e)}
                      className={styles.groupTag}
                    >
                      {group.name}
                    </Tag>
                  ) : null
                })
              })()}
            </Flex>
          </Button>

          {!isMenuListHide && (
            <GroupMenuItems
              action={action}
              groups={groups}
              selectedGroups={selectedGroups}
              onSelectedGroupsChange={handleSelectedGroupsChange}
              client={client}
              selectedClientIds={selectedClientIds}
              setSelectedClientIds={setSelectedClientIds}
            />
          )}
        </>
      )}
    </div>
  )
}

GroupDropdown.propTypes = {
  action: PropTypes.string,
  menuState: PropTypes.bool,
  isMenuListHide: PropTypes.bool,
  selectedGroups: PropTypes.array,
  client: PropTypes.object,
  selectedClientIds: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),
  onSelectedGroupsChange: PropTypes.func,
  moduleName: propTypes.string,
  setSelectedClientIds: PropTypes.func,
  groupAction: PropTypes.string
}

export default GroupDropdown
